import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import type { LocationAddressChange } from "@models/organization";
import { formatDate } from "@utils/formatter";
import { formatAddress } from "../_helpers/addressForm";
import { downloadErrorMessage } from "../_helpers/certReview";
import { useFileDownload } from "../_helpers/useFileDownload";
import { LocationStatusBadge } from "./LocationStatusBadge";

interface LocationAddressHistoryProps {
    locationId: string;
    // Newest first, as the API sends them.
    changes: LocationAddressChange[];
}

// The name a certificate is saved under when the server sends none (its
// Content-Disposition is not always readable cross-origin).
const CERTIFICATE_SAVE_NAME = "resale-certificate";

// Every admin edit of a location's address (C6: "each change is recorded"),
// read-only, in the Edit Location drawer: when, by whom, from and to, the
// status move a change of state made, and the certificate it carried. Nothing
// at all when the address was never edited.
export const LocationAddressHistory = ({
    locationId,
    changes,
}: LocationAddressHistoryProps) => {
    const { downloadLocationFile } = useLocationApi();
    const {
        downloadingId,
        error: downloadError,
        start: startDownload,
    } = useFileDownload(
        locationId,
        (fileId) => downloadLocationFile(locationId, fileId),
        (error) => downloadErrorMessage(error, "certificates"),
    );

    if (changes.length === 0) {
        return null;
    }

    return (
        <section className="mt-6">
            <h3 className="mb-2 font-bold">Address history</h3>
            <ol className="border-l border-base-300 pl-4">
                {changes.map((change) => {
                    const certificateId = change.certificateFileId;

                    return (
                        <li key={change.id} className="mb-3 text-sm last:mb-0">
                            <p className="text-neutral/70">
                                <time dateTime={change.changedAt}>
                                    {formatDate(change.changedAt)}
                                </time>{" "}
                                by {change.changedByUserName}
                            </p>
                            <p>
                                {formatAddress(change.fromAddress)} →{" "}
                                {formatAddress(change.toAddress)}
                            </p>
                            {change.fromStatus !== change.toStatus && (
                                // A div: the badges render divs, which a <p>
                                // cannot contain.
                                <div className="flex flex-wrap items-center gap-1">
                                    Status:
                                    <LocationStatusBadge
                                        status={change.fromStatus}
                                    />
                                    →
                                    <LocationStatusBadge
                                        status={change.toStatus}
                                    />
                                </div>
                            )}
                            {certificateId && (
                                <>
                                    <button
                                        type="button"
                                        className="link link-primary disabled:cursor-not-allowed disabled:opacity-60"
                                        disabled={downloadingId !== null}
                                        aria-busy={downloadingId === certificateId}
                                        onClick={() =>
                                            void startDownload({
                                                id: certificateId,
                                                fileName: CERTIFICATE_SAVE_NAME,
                                            })
                                        }
                                    >
                                        View certificate
                                    </button>
                                    {downloadError?.fileId === certificateId && (
                                        <p role="alert" className="text-error">
                                            {downloadError.message}
                                        </p>
                                    )}
                                </>
                            )}
                        </li>
                    );
                })}
            </ol>
        </section>
    );
};
